import type { Namespace, NamespaceKeys } from '@fsd/shared';
import { useTranslation as useI18nTranslation } from 'react-i18next';

/**
 * Typed translation hook
 * Provides autocomplete for translation keys based on namespace
 */
export function useTranslation<NS extends Namespace>(namespace: NS) {
  const { t: tOriginal, i18n } = useI18nTranslation(namespace);

  return {
    t: (key: NamespaceKeys<NS>, options?: Record<string, unknown>) =>
      tOriginal(key as string, options),
    i18n,
    locale: i18n.language as 'pt-BR' | 'en-US' | 'es-ES',
    changeLocale: (locale: 'pt-BR' | 'en-US' | 'es-ES') => {
      i18n.changeLanguage(locale);
    },
  };
}

/**
 * Typed translation hook without namespace (uses all keys)
 */
export function useTranslationWithoutNamespace() {
  const { t, i18n } = useI18nTranslation();

  return {
    t: (key: string, options?: Record<string, unknown>) => t(key, options),
    i18n,
    locale: i18n.language as 'pt-BR' | 'en-US' | 'es-ES',
    changeLocale: (locale: 'pt-BR' | 'en-US' | 'es-ES') => {
      // changeLanguage automatically persists to localStorage and cookie via LanguageDetector
      // See apps/web/src/i18n/config.ts for detection configuration
      i18n.changeLanguage(locale);
    },
  };
}
